'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Card } from '@/ui/primatives';
import Tag from '@/ui/Tag';
import * as Styled from './styled';
import Link from 'next/link';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { IRepository, IResourceVersion, IUser } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { IDataFrameResponse, IDataFrameSchema } from '@/lib/dataFrameTypes';
import EvaluationWizard, { IEvaluationFormData } from '@/components/Repository/Evaluations/EvaluationWizard';
import { createEvaluation, IEvaluation, TEvaluationInputType, TEvaluationOutputType } from '@/lib/evaluationsApi';
import { TEvaluationModel } from '@/lib/evaluationModelsApi';
import { getEvaluationTarget } from '@/components/Repository/Evaluations/lib';

import useEvaluation from '@/hooks/useEvaluation';

import DataframeView from './DataframeView';
import RunStatus from '@/components/Repository/Evaluations/RunStatus';
import JsonView from '@/components/JsonView';
import useAnchorTag from '@/hooks/useAnchorTag';
import { sectionNavigationPath } from '../../consts';

const POLLING_INTERVAL = 1 * 1000; // 1 second

type TViewMode = 'test' | 'run' | 'json' | 'history';

const usePollingRevalidate = (status: string, evaluation: IEvaluation | undefined, revalidate: () => void) => {
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    // TODO: Make the bailout status more universal
    const isRunningStatus = !['completed', 'error', 'cancelled'].includes(status);
    const hasClosingCommit = !!evaluation?.closing_commit;
    const shouldRevalidate = isRunningStatus && !hasClosingCommit;

    if (shouldRevalidate) {
      intervalId = setInterval(() => {
        revalidate();
      }, POLLING_INTERVAL);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status, evaluation, revalidate]);
};

const ViewEvalution: React.FC<{
  currentUser?: IUser;
  repository: IRepository;
  evaluation: IEvaluation;
  availableModels: TEvaluationModel[];
  dataFrameResponse?: IDataFrameResponse;
  resourceVersion: IResourceVersion;
  schema: IDataFrameSchema;
  rowCount?: number;
  authToken?: string;
  iterations?: IEvaluation[];
}> = ({
  currentUser,
  repository,
  evaluation: initialEvaluation,
  availableModels,
  schema,
  dataFrameResponse: initialDataFrameResponse,
  resourceVersion,
  rowCount,
  iterations,
}) => {
  const router = useRouter();
  const [dataFrameResponse, setDataFrameResponse] = useState(initialDataFrameResponse);

  const anchorTag = useAnchorTag();
  const defaultViewMode = initialEvaluation?.is_sample ? 'test' : 'run';
  const [viewMode, setViewMode] = useState<TViewMode>((anchorTag as TViewMode) || defaultViewMode);
  const [createError, setCreateError] = useState<IResponseError | undefined>(undefined);

  const { evaluation, status, error, revalidate } = useEvaluation({
    repository,
    evaluationId: initialEvaluation.id,
    evaluation: initialEvaluation,
  });

  useEffect(() => {
    if (anchorTag && ['json', 'history', 'test', 'run'].includes(anchorTag)) {
      setViewMode(anchorTag as TViewMode);
    } else {
      setViewMode(defaultViewMode);
    }
  }, [anchorTag]);

  const percentComplete = ((evaluation?.progress?.processed || 0) / (evaluation?.progress?.total || 1)) * 100;

  useEffect(() => {
    setDataFrameResponse(initialDataFrameResponse);
  }, [initialDataFrameResponse]);

  const onRunSample = async (formEval: IEvaluationFormData) => {
    setCreateError(undefined);
    console.log('onRunSample > formEval', formEval);

    const [inputType, outputType] = formEval.type.split('|');
    // const formData = { ...formEval, input_type: inputType, output_type: outputType };

    const { evaluation: newEvaluation, error: createEvaluationError } = await createEvaluation({
      name: formEval.name,
      repository,
      resource: resourceVersion as IResourceVersion,
      prompt: formEval.prompt,
      input_type: inputType as TEvaluationInputType,
      output_type: outputType as TEvaluationOutputType,
      is_sample: true,
      sample_count: formEval.sample_count,
      model_id: formEval.model_id as string,
      target_column: formEval.target_column,
      target_branch: formEval.target_branch,
      target_path: formEval.target_path,
      parent_id: formEval.id,
      master_id: formEval.master_id,
    });

    if (createEvaluationError) {
      setCreateError(createEvaluationError);
      return;
    }

    const newEvaluationId = newEvaluation?.id;
    console.log('newEvaluationId', newEvaluationId);
    router.push(`/${repository.namespace}/${repository.name}/${sectionNavigationPath}/${newEvaluationId}`);
  };

  const onRunEvaluation = async (formEval: IEvaluationFormData) => {
    setCreateError(undefined);
    const [inputType, outputType] = formEval.type.split('|');
    const { evaluation: newEvaluation, error: createEvaluationError } = await createEvaluation({
      name: formEval.name,
      repository,
      resource: resourceVersion as IResourceVersion,
      prompt: formEval.prompt,
      input_type: inputType as TEvaluationInputType,
      output_type: outputType as TEvaluationOutputType,
      is_sample: false,
      model_id: formEval.model_id as string,
      target_column: formEval.target_column,
      target_branch: formEval.target_branch,
      target_path: formEval.target_path,
      parent_id: formEval.id,
      master_id: formEval.master_id,
      auto_commit: formEval.auto_commit,
      commit_message: formEval.commit_message,
      batch_size: formEval.batch_size,
    });

    if (createEvaluationError) {
      setCreateError(createEvaluationError);
      return;
    }

    const newEvaluationId = newEvaluation?.id;
    router.push(`/${repository.namespace}/${repository.name}/${sectionNavigationPath}/${newEvaluationId}`);
  };

  usePollingRevalidate(status, evaluation, revalidate);

  const target = getEvaluationTarget(repository, evaluation!);
  const isCommitted = !!target?.commit;
  const isActiveInWorkspace = viewMode === 'run' && !isCommitted;
  // const isCommitted = !!evaluation?.closing_commit;
  // const isActiveInWorkspace = viewMode === 'run' && !evaluation?.closing_commit;
  const isSampleWithData = evaluation?.is_sample && ['started', 'completed'].includes(evaluation?.status);
  const showSampleData = viewMode === 'test' && isSampleWithData;

  return (
    <Styled.ViewEvaluation>
      {evaluation?.conflict_target_branch && (
        <Card.Warning compressed style={{ marginBottom: 20 }}>
          <div>
            <ExclamationTriangleIcon style={{ verticalAlign: 'middle', marginRight: 10 }} />
            Your evaluation results conflicted with the desired target branch. results have been placed in a new branch{' '}
            <Link
              href={`/${repository.namespace}/${repository.name}/branches/${evaluation?.conflict_target_branch}`}
              className="link-underline"
            >
              {evaluation?.conflict_target_branch}
            </Link>
          </div>
        </Card.Warning>
      )}
      <Styled.Content>
        {currentUser?.role === 'admin' && viewMode === 'json' ? (
          <div style={{ flex: 1, padding: 25, borderLeft: '1px solid #cccccc' }}>
            <JsonView json={evaluation} rootNodeName="evaluation" />
          </div>
        ) : (
          <EvaluationWizard
            repository={repository}
            evaluation={evaluation as IEvaluation}
            availableModels={availableModels}
            schema={schema}
            onRunEvaluation={onRunEvaluation}
            onRunSample={onRunSample}
            error={createError}
          />
        )}

        <div style={{ boxShadow: 'none' }} ddata-tutorialid="evaluation-run-status">
          <RunStatus repository={repository} evaluation={evaluation as IEvaluation} rowCount={rowCount}>
            {evaluation?.is_sample && <Tag variant="primary">Sample Results</Tag>}
          </RunStatus>
        </div>

        {status === 'error' && (
          <Card.Error>
            <div>
              <b>Error: </b> {evaluation?.error_message}
            </div>
          </Card.Error>
        )}

        <div data-tutorialid="evaluation-dataframe" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {isActiveInWorkspace && (
            <DataframeView.Workspace repository={repository} evaluation={evaluation as IEvaluation} />
          )}

          {showSampleData && (
            <DataframeView.Sample
              repository={repository}
              evaluation={evaluation as IEvaluation}
              dataFrameResponse={dataFrameResponse as unknown as IDataFrameResponse}
            />
          )}

          {isCommitted && (
            <DataframeView.Loader
              repository={repository}
              evaluation={evaluation as IEvaluation}
              dataFrameResponse={dataFrameResponse as unknown as IDataFrameResponse}
            />
          )}
        </div>
      </Styled.Content>
    </Styled.ViewEvaluation>
  );
};

export default ViewEvalution;
